import { supabase } from "@/lib/supabase";

/**
 * Download avatar file from storage
 */
export async function downloadAvatar(storageName: string, path: string): Promise<string> {
  if (!storageName.trim()) {
    throw new Error("Storage name is required");
  }

  if (!path.trim()) {
    throw new Error("File path is required");
  }

  const { data, error } = await supabase.storage.from(storageName).download(path);

  if (error) {
    throw new Error(`Failed to download file: ${error.message}`);
  }

  return URL.createObjectURL(data);
}

/**
 * Get public URL for an avatar in a public bucket
 */
export function getPublicUrl(storageName: string, path: string): string {
  if (!storageName.trim()) {
    throw new Error("Storage name is required");
  }

  if (!path.trim()) {
    throw new Error("File path is required");
  }

  const { data } = supabase.storage.from(storageName).getPublicUrl(path);

  if (!data.publicUrl) {
    throw new Error("Failed to get public URL for avatar");
  }

  return data.publicUrl;
}

/**
 * Get URL for an avatar in a private bucket
 */
export async function getSignedUrl(
  storageName: string,
  path: string,
  expiresInSeconds = 60 * 60, // 1 hour
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(storageName)
    .createSignedUrl(path, expiresInSeconds);

  if (error) {
    throw new Error(`Failed to get signed URL for avatar ${error.message}`);
  }
  return data.signedUrl;
}

/**
 * Upload an avatar file to storage
 */
export async function uploadAvatar({
  storageName,
  filePath,
  file,
  robohashUrl,
}: {
  storageName: string;
  filePath: string;
  file?: File;
  robohashUrl?: string;
}): Promise<string> {
  // Safety: exactly one source must be provided
  if (file && robohashUrl) {
    throw new Error("Provide either file or robohashUrl, not both.");
  }

  let uploadData: File | Blob;

  if (file) {
    uploadData = file;
  } else if (robohashUrl) {
    const response = await fetch(robohashUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch robohash image: ${response.status} ${response.statusText}`);
    }

    const blob = await response.blob();

    uploadData = blob;
  } else {
    throw new Error(`Either file or robohashUrl must be provided.`);
  }

  const { error } = await supabase.storage
    .from(storageName)
    .upload(filePath, uploadData, { upsert: true });

  if (error) {
    throw new Error(`Failed to upload avatar: ${error.message}`);
  }

  return filePath;
}

/**
 * Delete an avatar file from storage
 */
export async function deleteAvatar(storageName: string, filePath: string): Promise<void> {
  const { error } = await supabase.storage.from(storageName).remove([filePath]);

  if (error) {
    throw new Error(`Failed to delete avatar: ${error.message}`);
  }
}
